import { useState } from "react";
import * as z from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { resetPassword } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/components/ui/use-toast";

export function ResetPasswordForm() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // Zod schema with translations
  const formSchema = z.object({
    email: z.string()
      .email(t("validation.invalidEmail"))
      .min(1, t("validation.required")),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsLoading(true);
    try {
      await resetPassword(values.email);
      setEmailSent(true);
      toast({
        title: t("resetPassword.success"),
        description: t("resetPassword.checkEmail"),
      });
    } catch (error: any) {
      const errorCode = error.code || "default";
      toast({
        title: t("errors.default"),
        description: t(`errors.${errorCode}`, { defaultValue: t("errors.default") }),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }

  if (emailSent) {
    return (
      <Card className="w-full shadow-lg border-primary/10">
        <CardContent className="pt-6">
          <Alert className="border-primary/20 bg-primary/5">
            <AlertDescription>
              <div className="space-y-2">
                <p className="font-semibold">{t("resetPassword.success")}</p>
                <p className="text-sm">{t("resetPassword.checkEmail")}</p>
              </div>
            </AlertDescription>
          </Alert>
          <div className="mt-6 text-center">
            <a href="/login" className="text-sm text-primary hover:underline">
              {t("resetPassword.backToLogin")}
            </a>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full shadow-lg border-primary/10">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl font-bold text-center text-primary">
          {t("resetPassword.title")}
        </CardTitle>
        <CardDescription className="text-center">
          {t("resetPassword.subtitle")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Email Field */}
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("resetPassword.email")}</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder={t("register.emailPlaceholder")}
                      autoComplete="email"
                      dir="ltr"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-primary to-secondary hover:opacity-90"
              size="lg"
              disabled={isLoading}
            >
              {isLoading ? t("common.loading") : t("resetPassword.sendLink")}
            </Button>
          </form>
        </Form>

        {/* Back to Login Link */}
        <div className="mt-6 text-center text-sm">
          <a href="/login" className="text-primary hover:underline">
            {t("resetPassword.backToLogin")}
          </a>
        </div>
      </CardContent>
    </Card>
  );
}
